/**
 * ContentCalendar — month-grid view of every content draft with a
 * `scheduled_at` (and any approved drafts without one bucketed in an
 * "Unscheduled" rail at the side).
 *
 * Each cell is a calendar day.  Drafts land on their scheduled day with a
 * coloured pill per platform.  Click a pill to open the approval thread.
 * Status colours follow the platform's brand accent + a status outline:
 *   - in_review → gold border
 *   - approved → solid emerald
 *   - executed (posted) → grey (already shipped)
 *   - rejected → rose strike-through
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Calendar, Twitter, Linkedin, Instagram, Youtube,
  Clock, Check, CircleSlash,
} from 'lucide-react';

const PLATFORM_META = {
  twitter:   { Icon: Twitter,   accent: '#1DA1F2' },
  linkedin:  { Icon: Linkedin,  accent: '#0A66C2' },
  instagram: { Icon: Instagram, accent: '#E4405F' },
  youtube:   { Icon: Youtube,   accent: '#FF0000' },
};

const STATUS_OUTLINE = {
  in_review: 'border-[#8B3FC5] bg-[#FFF8E5]',
  approved:  'border-emerald-300 bg-emerald-50',
  executed:  'border-slate-200 bg-slate-50 opacity-60',
  rejected:  'border-rose-200 bg-rose-50 line-through opacity-50',
};

const STATUS_ICON = {
  in_review: Clock,
  approved:  Check,
  executed:  Check,
  rejected:  CircleSlash,
};

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildMonthGrid(viewDate) {
  // Returns 6 weeks of 7 days starting from the first Sunday on or before
  // the 1st of the month — keeps the grid rectangular regardless of month.
  const first = startOfMonth(viewDate);
  const grid = [];
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    grid.push(d);
  }
  return grid;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July',    'August',   'September', 'October', 'November', 'December',
];

export default function ContentCalendar({ drafts = [] }) {
  const [view, setView] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const monthGrid = useMemo(() => buildMonthGrid(view), [view]);

  // Index drafts by ymd of scheduled_at (or posted_at as fallback for executed)
  const byDay = useMemo(() => {
    const map = {};
    const unscheduled = [];
    drafts.forEach((d) => {
      const p = d.payload || {};
      const isoCandidate = p.scheduled_at || p.posted_at;
      if (!isoCandidate) { unscheduled.push(d); return; }
      const dt = new Date(isoCandidate);
      if (Number.isNaN(dt.getTime())) { unscheduled.push(d); return; }
      const key = ymd(dt);
      (map[key] = map[key] || []).push({ ...d, _dt: dt });
    });
    return { map, unscheduled };
  }, [drafts]);

  const isToday = (d) => ymd(d) === ymd(new Date());
  const isCurMonth = (d) => d.getMonth() === view.getMonth();

  const total = drafts.length;
  const scheduledCount = total - byDay.unscheduled.length;

  return (
    <div className="mt-6 ve-card p-5" data-testid="content-calendar">
      <header className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar size={20} className="text-[#5E1F8B]"/>
          <div>
            <h3 className="display text-lg font-bold leading-tight">
              {MONTH_NAMES[view.getMonth()]} {view.getFullYear()}
            </h3>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
              {scheduledCount} of {total} draft{total === 1 ? '' : 's'} scheduled
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  data-testid="calendar-prev-month"
                  className="ve-btn-secondary text-xs">
            <ChevronLeft size={12}/> Prev
          </button>
          <button onClick={() => { const t = new Date(); setView(new Date(t.getFullYear(), t.getMonth(), 1)); }}
                  data-testid="calendar-today"
                  className="ve-btn-secondary text-xs">
            Today
          </button>
          <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  data-testid="calendar-next-month"
                  className="ve-btn-secondary text-xs">
            Next <ChevronRight size={12}/>
          </button>
        </div>
      </header>

      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="text-center py-1">{wd}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
        {monthGrid.map((d, i) => {
          const key = ymd(d);
          const items = byDay.map[key] || [];
          const inMonth = isCurMonth(d);
          return (
            <div key={i}
                 data-testid={`calendar-cell-${key}`}
                 className={`min-h-[90px] rounded-md border p-1.5 text-xs ${
                   inMonth ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                 } ${isToday(d) ? 'ring-2 ring-[#8B3FC5]' : ''}`}>
              <div className={`flex items-center justify-between text-[10px] font-bold mb-1 ${
                isToday(d) ? 'text-[#5E1F8B]' : 'text-slate-500'
              }`}>
                <span>{d.getDate()}</span>
                {items.length > 0 && (
                  <span className="text-[9px] bg-slate-100 text-slate-600 px-1 rounded">
                    {items.length}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((it) => {
                  const p = it.payload || {};
                  const meta = PLATFORM_META[p.platform] || PLATFORM_META.twitter;
                  const Icon = meta.Icon;
                  const StatusIcon = STATUS_ICON[it.status] || Clock;
                  return (
                    <Link key={it.request_id}
                          to={`/dashboard/approvals/${it.request_id}`}
                          data-testid={`calendar-item-${it.request_id}`}
                          title={`${p.platform} · ${(p.body_text || '').slice(0, 80)}`}
                          className={`block rounded border px-1.5 py-0.5 truncate hover:shadow-sm transition-all ${STATUS_OUTLINE[it.status] || ''}`}>
                      <div className="flex items-center gap-1 text-[10px]">
                        <Icon size={9} style={{ color: meta.accent }}/>
                        <span className="truncate font-bold">
                          {(p.body_text || '').slice(0, 22)}
                        </span>
                        <StatusIcon size={9} className="ml-auto opacity-60"/>
                      </div>
                    </Link>
                  );
                })}
                {items.length > 3 && (
                  <div className="text-[9px] text-slate-500 font-bold pl-1">
                    +{items.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {byDay.unscheduled.length > 0 && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
            Unscheduled ({byDay.unscheduled.length})
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="calendar-unscheduled">
            {byDay.unscheduled.map((it) => {
              const p = it.payload || {};
              const meta = PLATFORM_META[p.platform] || PLATFORM_META.twitter;
              const Icon = meta.Icon;
              return (
                <Link key={it.request_id}
                      to={`/dashboard/approvals/${it.request_id}`}
                      data-testid={`calendar-unscheduled-item-${it.request_id}`}
                      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold hover:shadow-sm ${STATUS_OUTLINE[it.status] || ''}`}>
                  <Icon size={10} style={{ color: meta.accent }}/>
                  <span className="truncate max-w-[140px]">{(p.body_text || '').slice(0, 24)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
